import { useEffect } from 'react';
import NiceModal from '@ebay/nice-modal-react';
import VoiceAgentModal from '@components/features/modals/elevenlabs/VoiceAgentModal';
import { useRouter } from 'next/router';
import { consumePendingVoiceAgent, VOICE_AGENT_QUERY_PARAM } from '@utils/voice-agent/showVoiceAgentModalWithAuth';

/**
 * @description Hook that auto-opens VoiceAgentModal after a login redirect.
 * It checks for the `openVoiceAgent=1` query param and saved params in sessionStorage.
 * After opening the modal, it cleans up the query param from the URL and removes the sessionStorage flag.
 *
 * Place this hook in `_app.tsx` so it runs globally on every page.
 */
export const useVoiceAgentModalAutoOpen = () => {
  const router = useRouter();

  useEffect(() => {
    // Check if the URL has the openVoiceAgent flag
    if (typeof window === 'undefined') return;

    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get(VOICE_AGENT_QUERY_PARAM) !== '1') return;

    // Consume the pending params from sessionStorage
    const pending = consumePendingVoiceAgent();
    if (!pending) return;

    // Show the modal with saved params
    void NiceModal.show(VoiceAgentModal, { agentId: pending.agentId, locale: pending.locale });

    // Clean up the query param from URL without triggering navigation
    searchParams.delete(VOICE_AGENT_QUERY_PARAM);
    const cleanSearch = searchParams.toString();
    const cleanUrl = `${window.location.pathname}${cleanSearch ? `?${cleanSearch}` : ''}`;
    window.history.replaceState(window.history.state, '', cleanUrl);
  }, [router.asPath]); // Re-run when route changes (after login redirect)
};
